import { forwardRef, useEffect, useImperativeHandle } from 'react';
import type { ProductPageMode, ManufacturerFormData } from '../../../types';
import { FormSectionTitle, FormField, FormInput } from '@practium/ui';
import { useManufacturerForm } from '../../../hooks/useManufacturerForm';
import styles from '../../ProductFormCard/ProductFormCard.module.css';

export interface ManufacturerFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

export interface ManufacturerFormProps {
	mode?: ProductPageMode;
	initialData?: ManufacturerFormData;
	onSubmit: () => void;
	onLoadingChange?: (loading: boolean) => void;
	id?: string;
}

export const ManufacturerForm = forwardRef<ManufacturerFormRef, ManufacturerFormProps>(
	function ManufacturerForm({ mode = 'add', initialData, onSubmit, onLoadingChange, id }, ref) {
		const { form, errors, isLoading, set, setCnpj, handleSubmit, isDirty } = useManufacturerForm({
			initialData,
			onSubmit,
			id
		});

		useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

		useEffect(() => {
			onLoadingChange?.(isLoading);
		}, [isLoading, onLoadingChange]);

		const isView = mode === 'view';

		return (
			<div className={styles.form}>
				<div className={styles.section}>
					<FormSectionTitle>Informações Gerais</FormSectionTitle>
					<div className={styles.sectionFields}>
						<FormField label="Nome Comercial" required={!isView} error={errors.tradingName}>
							{isView ? (
								<span className={styles.viewText}>{form.tradingName || '—'}</span>
							) : (
								<FormInput
									placeholder="Ex.: Fertilizantes Heringer S.A."
									value={form.tradingName}
									onChange={(e) => set('tradingName', e.target.value)}
									hasError={!!errors.tradingName}
								/>
							)}
						</FormField>
						<div className={styles.row2}>
							<FormField label="Razão Social" error={errors.businessName}>
								{isView ? (
									<span className={styles.viewText}>{form.businessName || '—'}</span>
								) : (
									<FormInput
										placeholder="Ex.: Heringer"
										value={form.businessName}
										onChange={(e) => set('businessName', e.target.value)}
										hasError={!!errors.businessName}
									/>
								)}
							</FormField>
							<FormField label="CNPJ">
								{isView ? (
									<span className={styles.viewText}>{form.cnpj || '—'}</span>
								) : (
									<FormInput
										placeholder="00.000.000/0000-00"
										value={form.cnpj}
										onChange={(e) => setCnpj(e.target.value)}
									/>
								)}
							</FormField>
						</div>
					</div>
				</div>
			</div>
		);
	},
);
